/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslationTupleOrId } from '@gh/core-mls';
import { ActionFlow, getRouteInitData, getRouteParentTitle, NumberTypes } from '@gh/common-shared';
import { ActivatedRoute } from '@angular/router';
import {
  PbDetails,
  PbDetailsSave,
  PbHousePlan,
  PbHousePlanRequest,
} from '../../resources/price-book.model';
import {
  createFilteredDataSource,
  emptySelectionModel,
  FilteredDataSource,
  mapSlicedDataItems,
  SelectionModel,
} from '@gh/core-data';
import {
  buildNumberColumn,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  RowPointer,
} from '@gh/core-ui-grid';
import { PriceBookDetailsBusinessService } from '../../resources/price-book-details-business.service';
import {
  AbstractFormComponent,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  getPbHousePlanId,
} from '@gh/builder-shared';
import { PbBulkUpdateHPMarkup } from '../../resources/pb-bulk-update-markup.model';
import {
  PbBulkUpdateHpMarkupFormState,
} from './pb-bulk-update-hp-markup-form.service';
import {
  LIGHTGREEN_MARKER_COLOR,
  RED_MARKER_COLOR,
  renderColoredArrow,
  rendererIconByName,
  ROW_DIRTY_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { omit } from 'lodash';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { NotificationService, WaitingService } from '@gh/core-ui';
import { DirtyType } from '@gh/common-shared-data';

export type PbHousePlanTable = PbHousePlan & {
  newQqMarkup?: any;
  newTotal?: any;
  trendTotal?: any;
};

@Component({
  standalone: false,
  selector: 'gh-price-book-bulk-update-hp',
  templateUrl: 'price-book-bulk-update-hp.component.html',
  styleUrls: ['price-book-bulk-update-hp.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ PbBulkUpdateHpMarkupFormState ],
})
export class PriceBookBulkUpdateHPComponent extends AbstractFormComponent<PbBulkUpdateHPMarkup> implements OnInit {
  loadedData: PbHousePlanTable[];
  backupData: PbHousePlan[];
  priceBook: PbDetails;
  canBeSelectedCount: number;
  gridDefinition: LayoutBasedGridDefinition;
  finderDataSource: FilteredDataSource<RowPointer, string>;
  targetSelectionModel = emptySelectionModel();
  isRowDisabledBound = this.isRowDisabled.bind(this);

  private gridApi: GridApi | undefined | null;

  get isProcessEnabled(): boolean {
    if (!this.form) {
      return false;
    }
    const { overhead, contingency, profit, qqMarkup } = this.getControlsValue();
    const hasChangesInControls = (!!parseFloat(profit) && profit !== 0) || (!!parseFloat(contingency) && contingency !== 0) ||
      (!!parseFloat(qqMarkup) && qqMarkup !== 0) || (!!parseFloat(overhead) && overhead !== 0);
    if (this.targetSelectionModel.isExcludeMode) {
      return hasChangesInControls;
    }
    return hasChangesInControls && !!this.targetSelectionModel.idList.length;
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_price_book';
  }

  get isGridSelectable(): boolean {
    if (!this.form) {
      return false;
    }
    const { overhead, contingency, profit, qqMarkup } = this.getControlsValue();
    return this.canBeSelectedCount > 0 && (!!overhead || !!contingency || !!profit || !!qqMarkup);
  }

  constructor(
    private notificationService: NotificationService,
    public form: PbBulkUpdateHpMarkupFormState,
    private route: ActivatedRoute,
    private waitingService: WaitingService,
    private changeDetectorRef: ChangeDetectorRef,
    private gridDefinitionFactory: GridDefinitionFactory,
    private businessService: PriceBookDetailsBusinessService,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    ) {
    super();
    this.canBeSelectedCount = 0;
    this.loadedData = [];
  }
  ngOnInit(): void {
    const { priceBookHousePlansGridLayout, priceBook } = getRouteInitData(this.route);
    this.priceBook = priceBook;
    this.loadData();
    this.setGridDefinition();
    this.gridDefinition.bind(priceBookHousePlansGridLayout);
    this.finderDataSource = mapSlicedDataItems(
      createFilteredDataSource(
        (filter: string) => this.businessService.findRelatedHousePlansAsDataSource({
                                                                                       priceBookId: this.priceBook.priceBookId,
                                                                                       housePlans: [],
                                                                                     searchString: filter,
                                                                                   })),
      (item) => {
        const row = this.gridApi?.getRowNode(getPbHousePlanId(item));
        return {rn: row?.rowIndex || 0, name: item.code };
      });

    this.form.build({housePlans: [], overhead: 0, contingency: 0, qqMarkup: 0, profit: 0, total: 0});
    this.form.getControl('overhead').valueChanges.subscribe(() =>  this.setTotal());
    this.form.getControl('qqMarkup').valueChanges.subscribe(() => this.setTotal());
    this.form.getControl('contingency').valueChanges.subscribe(() => this.setTotal());
    this.form.getControl('profit').valueChanges.subscribe(() => this.setTotal());
  }

  setGridDefinition(): void {
    this.gridDefinition = this.gridDefinitionFactory
      .buildLayoutBased(
        {
          isQqAllowed: {
            cellRenderer: truthyCellRenderer(rendererIconByName('done')),
          },
          newQqMarkup: {
            ...buildNumberColumn(NumberTypes.Percents_2),
            valueGetter:  ({ data }: { data: PbHousePlanTable }): string => {
              if (!this.isSelected(data) || this.isRowDisabled(data) || (!data.newQqMarkup && data.newQqMarkup !== 0)) {
                return '';
              }
              return data.newQqMarkup;
            },
            cellClassRules: {
              'warn-color': ({ data }: { data: PbHousePlanTable }) => this.isIncreaseColor(data, this.form.getControl('qqMarkup')?.value),
              'success-color': ({ data }: { data: PbHousePlanTable }) => this.isDecreaseColor(data, this.form.getControl('qqMarkup')?.value),
            },
          },
          qqMarkupPct: buildNumberColumn(NumberTypes.Percents_2),
          floorArea: buildNumberColumn(NumberTypes.Quantity_2),
          cost: buildNumberColumn(NumberTypes.Price),
          overheadMarkupPct: {
            ...buildNumberColumn(NumberTypes.Percents_2),
            cellClassRules: {
              'warn-color': ({ data }: { data: PbHousePlanTable }) => this.isIncreaseColor(data, this.form.getControl('overhead')?.value),
              'success-color': ({ data }: { data: PbHousePlanTable }) => this.isDecreaseColor(data, this.form.getControl('overhead')?.value),
            },
          },
          contingencyMarkupPct: {
            ...buildNumberColumn(NumberTypes.Percents_2),
            cellClassRules: {
              'warn-color': ({ data }: { data: PbHousePlanTable }) => this.isIncreaseColor(data, this.form.getControl('contingency')?.value),
              'success-color': ({ data }: { data: PbHousePlanTable }) => this.isDecreaseColor(data, this.form.getControl('contingency')?.value),
            },
          },
          totalPct: buildNumberColumn(NumberTypes.Percents_2),
          newTotal: {
            ...buildNumberColumn(NumberTypes.Percents_2),
            cellClassRules: {
              'warn-color': ({ data }: { data: PbHousePlanTable }) =>  this.isIncreaseColor(data, this.form.getControl('total')?.value),
              'success-color': ({ data }: { data: PbHousePlanTable }) => this.isDecreaseColor(data, this.form.getControl('total')?.value),
            },
            valueGetter:  ({ data }: { data: PbHousePlanTable }): string => {
              if (!this.isSelected(data) || this.isRowDisabled(data) || (!data.newTotal && data.newTotal !== 0)) {
                return '';
              }
              return data.newTotal;
            },
          },
          trendTotal: {
            cellRenderer: ({ data }: { data: PbHousePlanTable }): string => {
              const { total } = this.getControlsValue();
              if (!this.isSelected(data) || this.isRowDisabled(data) || !total) {
                return '';
              }
              return total > 0
                ? renderColoredArrow(RED_MARKER_COLOR)
                : renderColoredArrow(LIGHTGREEN_MARKER_COLOR, false);
            },
          },
          profitMarkupPct: {
            ...buildNumberColumn(NumberTypes.Percents_2),
            cellClassRules: {
              'warn-color': ({ data }: { data: PbHousePlan }) => this.isIncreaseColor(data, this.form.getControl('profit')?.value),
              'success-color': ({ data }: { data: PbHousePlan }) => this.isDecreaseColor(data, this.form.getControl('profit')?.value),
            },
          },
        },
        {
          rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
          getRowId: (params) => String(getPbHousePlanId(params.data)),
          rowClassRules: {
            [ROW_DIRTY_CLASS]: ({ data }: { data: PbHousePlanTable }) => data && this.isSelected(data),
          },
        });
  }

  getControlsValue(): any {
    const overhead = parseFloat(this.form.getControl('overhead').value) || 0;
    const contingency =  parseFloat(this.form.getControl('contingency').value) || 0;
    const profit =  parseFloat(this.form.getControl('profit').value) || 0;
    const total =  parseFloat(this.form.getControl('total').value) || 0;
    const qqMarkup =  parseFloat(this.form.getControl('qqMarkup').value) || 0;
    return {overhead, contingency, profit, total, qqMarkup} ;
  }

  setTotal(): void {
    const { overhead, contingency, profit } = this.getControlsValue();
    this.form.getControl('total').setValue(profit + contingency + overhead);
    this.deselectRows();
    this.recalculateData();
    this.gridApi?.redrawRows();
  }

  isDecreaseColor(data: PbHousePlan, value: number): boolean {
    return this.isSelected(data) && !this.isRowDisabled(data) && value < 0;
  }

  isIncreaseColor(data: PbHousePlan, value: number): boolean {
    return this.isSelected(data) && !this.isRowDisabled(data) && value > 0;
  }

    // deselect rows with data < 0
  deselectRows(): void {
    if (!this.targetSelectionModel.isExcludeMode && this.targetSelectionModel.idList.length) {
      this.loadedData.forEach((item) => {
        if (this.isRowDisabled(item) && this.targetSelectionModel.idList.includes(item.housePlanId)) {
          this.targetSelectionModel.idList.splice(this.targetSelectionModel.idList.indexOf(item.housePlanId), 1);
        }
      });
    } else if (this.targetSelectionModel.isExcludeMode) {
      this.loadedData.forEach((item) => {
        if (this.isRowDisabled(item) && !this.targetSelectionModel.idList.includes(item.housePlanId)) {
          this.targetSelectionModel.idList.push(item.housePlanId);
        }
      });
    }
    if (this.targetSelectionModel.isExcludeMode && this.targetSelectionModel.idList.length === this.loadedData.length) {
      this.targetSelectionModel.isExcludeMode = false;
    }
    this.changeDetectorRef.detectChanges();
  }

  get completeFlow(): ActionFlow<FormFlowState<any>> {
    return this.pipelines.complete(
      {
        execute: this.commonActions.execute(() => {
          const housePlans = this.targetSelectionModel.isExcludeMode ?
            this.loadedData.filter((item) => !this.targetSelectionModel.idList.includes(item.housePlanId)) :
            this.loadedData.filter((item) => this.targetSelectionModel.idList.includes(item.housePlanId));
          const detailsDto = {
            ...this.priceBook,
            costCenters: [],
            items: [],
            housePlans: housePlans.map((housePlan) =>
              omit({
                     ...housePlan,
                     qqMarkupPct: housePlan.newQqMarkup,
                     totalPct: housePlan.newTotal,
                     dirtyType: DirtyType.Updated,
                   },
                   'newQqMarkup',
                   'newTotal',
                   'trendTotal',
              )),
            isPersist: true,
          } as PbDetailsSave;
          return this.businessService.save(omit(detailsDto, 'housePlanIds', 'repriceDate'));
        }),
        finish: (fs: FormFlowState<any>) => {
          this.priceBook.version = fs.value.version;
          this.targetSelectionModel.isExcludeMode = false;
          this.targetSelectionModel.idList = [];
          this.gridApi?.deselectAll();
          this.loadData();
          },
        error: (error: any) => this.notificationService.processAll(error),
      });
  }

  onTargetSelectionChanged(model: SelectionModel): void {
    this.targetSelectionModel = model;
    this.deselectRows();
    this.recalculateData();
    this.gridApi?.redrawRows();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  isSelected(data: PbHousePlan): boolean {
    if (!data) {
      return  false;
    }
    if (!this.targetSelectionModel.isExcludeMode) {
      return this.targetSelectionModel.idList.includes(data.housePlanId);
    } else {
      return !this.targetSelectionModel.idList.includes(data.housePlanId);
    }
  }

  isRowDisabled(data: PbHousePlanTable | any): boolean {
    if (!data || !this.form) {
      return true;
    }
    const { overhead, contingency, profit, qqMarkup, total } = this.getControlsValue();
    if (!qqMarkup && !profit && !contingency && !overhead) {
      return true;
    }
    const item = (data.id ? this.gridApi?.getRowNode(data.id)?.data : undefined) ?? data;
    const backUpItem = this.backupData.find((backup) => backup.housePlanId === item.housePlanId);
    if (!backUpItem) {
      return true;
    } else if (backUpItem.overheadMarkupPct + overhead < 0) {
      return true;
    } else if (backUpItem.qqMarkupPct + qqMarkup < 0) {
      return true;
    } else if (backUpItem.profitMarkupPct + profit < 0) {
      return true;
    } else if (backUpItem.contingencyMarkupPct + contingency < 0) {
      return true;
    } else if (backUpItem.totalPct + total < 0) {
        return true;
    } else {
      return false;
    }
  }

  recalculateData(): void {
    const { overhead, contingency, profit, qqMarkup, total } = this.getControlsValue();
    this.canBeSelectedCount = 0;
    this.loadedData.forEach((item) => {
      const backupItem = this.backupData.find((backupDataItem) => backupDataItem.housePlanId === item.housePlanId) || <any>{};
      const rowDisabled = this.isRowDisabled(item);
      if (!rowDisabled) {
        this.canBeSelectedCount++;
      }
      if (!this.isSelected(item) || rowDisabled) {
        for (const key in backupItem) {
          if (Object.prototype.hasOwnProperty.call(backupItem, key)) {
            item[key] = backupItem[key];
          }
        }
        delete item.newQqMarkup;
        delete item.newTotal;
        delete item.trendTotal;
      } else {
        item.newQqMarkup = qqMarkup ? qqMarkup + backupItem.qqMarkupPct : '';
        item.overheadMarkupPct = overhead + backupItem.overheadMarkupPct;
        item.contingencyMarkupPct = contingency + backupItem.contingencyMarkupPct;
        item.profitMarkupPct = profit + backupItem.profitMarkupPct;
        item.newTotal = total || total === 0 ? total + backupItem.totalPct : '';
        item.trendTotal = total > 0 ? 1 : total < 0 ? -1 : 0;
      }
    });
    if (this.canBeSelectedCount === 0) {
      this.targetSelectionModel.isExcludeMode = false;
      this.targetSelectionModel.idList = [];
    }
    this.changeDetectorRef.detectChanges();
  }

  onFocusOut(event: any, controlName: 'overhead' | 'contingency' | 'profit' | 'qqMarkup'): void {
    if (event.target.value === '') {
      this.form.getControl(controlName).setValue(0);
    }
  }

  private loadData(): void {
    this.targetSelectionModel = emptySelectionModel();
    const request = {
      priceBookId: this.priceBook.priceBookId,
      housePlans: [],
      version: this.priceBook.version,
    } as PbHousePlanRequest;
    this.businessService.getHousePlanListByPBId(request).pipe(this.waitingService.operator())
      .subscribe((res) => {
      this.loadedData = res;
      this.backupData = res.map((item) => ({...item}));
      this.changeDetectorRef.detectChanges();
    },           this.notificationService.processAll);
  }
}
